import { useState } from 'react';
import clsx from 'clsx';
import type {
  InteractionRequest,
  TableEditInteraction,
  SingleChoiceInteraction,
  MarketDefaultApplyInteraction,
} from '@arcanum-hq/shared-types';
import { Button } from '@/components/ui/Button';
import { TableGridEditor } from '@/components/builder/tool-results/TableGridEditor';
import { useDefaultMatchSuggestions } from '@/hooks/use-default-match-suggestions';
import { useApplyValue } from '@/hooks/use-apply-value';

/**
 * `<InteractionSurface>` — composer-slot host for structured human-in-the-loop
 * input forms (table-interaction-surface feature, 2026-06-09).
 *
 * Dispatches on `request.kind` and renders the appropriate form body inline,
 * REPLACING the chat composer zone while active. Dismiss restores the composer.
 *
 * ## Variants (v1)
 * - `table-edit`: editable table grid via the re-homed `<TableGridEditor>`.
 *   Submit path: `useTableInputsEdit` → `POST /api/conversations/:id/table-edit`
 *   (LOCKED #3 — direct write, NOT a synthesized turn, NOT via handleSend).
 * - `single-choice`: radio-button options stub (proves shell generalises per
 *   fidelity-spec § "Generalization"). NOT wired to any agent path in v1.
 * - All other kinds (`multi-choice` | `free-text` | `numeric` | `confirm`):
 *   declared in the shared-types union, not rendered — fall to null.
 *
 * ## Density
 * - `'workbench'`: wider padding (`px-6 py-3.5`), no grid height cap.
 * - `'narrative'`: tighter padding (`px-4 py-3`), grid `max-h-[220px]`.
 *
 * ## Props
 * - `request`: `InteractionRequest` (Zod-inferred, from `@arcanum-hq/shared-types`).
 * - `density`: layout tier — `'workbench'` (full composer) | `'narrative'`
 *   (slim NarrativeBar).
 * - `currentInputs`: forwarded to `<TableGridEditor>` for `table-edit` variant.
 *   Matches `useConversationViews().inputs` shape (value + source per named range).
 * - `onDismiss`: called when user clicks ✕ or Dismiss; host restores chat composer.
 * - `billingReadOnly`: disables Submit (allowance exhausted).
 * - `adminReadOnly`: when true the surface is suppressed (host should gate with
 *   `!adminReadOnly` before mounting — this prop is a defence-in-depth no-render
 *   guard).
 *
 * ## Read-only gates (design §11 risk #17)
 * - `billingReadOnly=true` → Submit disabled (but form still visible).
 * - `adminReadOnly=true` → renders null (host should NOT mount it in admin
 *   read-only context; this guard is defence-in-depth).
 *
 * ## market_default_apply arm (market-default-apply-ux Phase 2 Session 2.3)
 * - `eligibleInputs`: full `{name, displayName, dataType, description?}[]` from
 *   builder.tsx's inputDefinitions. Threaded to `<MarketDefaultApplyBody>` so the
 *   `useDefaultMatchSuggestions` hook can pass dataType to the suggestion endpoint.
 *   Cannot be derived from `request.options[]` — that shape has only `{id,label,hint?}`
 *   and lacks `dataType` (B2/I1 — divergence #8 in phase design).
 *   Optional so existing 6 arms are unaffected.
 */

export interface InteractionSurfaceProps {
  /** The interaction request — discriminated on `kind`. */
  request: InteractionRequest;
  /** Layout density: workbench (full composer) vs narrative (slim NarrativeBar). */
  density: 'workbench' | 'narrative';
  /**
   * Current input provenance forwarded to `<TableGridEditor>`.
   * Shape: `Record<namedRange, { value: unknown; source?: string }>`.
   * Optional — grid seeds from schema defaults when absent.
   */
  currentInputs?: Record<string, { value: unknown; source?: string }>;
  /** Called when the user dismisses without submitting. */
  onDismiss: () => void;
  /**
   * Billing allowance exhausted — disable Submit, leave form visible.
   * Matches the `billingReadOnly` convention in builder.tsx.
   */
  billingReadOnly?: boolean;
  /**
   * Admin read-only mode — suppress the surface entirely (renders null).
   * Host should gate with `!adminReadOnly` before mounting; this is defence-in-depth.
   */
  adminReadOnly?: boolean;
  /**
   * Full eligible-input definitions for the market_default_apply arm.
   * Shape: `{name, displayName, dataType, description?}[]` from builder.tsx's
   * inputDefinitions — carries `dataType` which `request.options[]` lacks.
   * Optional — only consumed by the market_default_apply dispatch branch.
   * market-default-apply-ux Phase 2 Session 2.3 (B2/I1).
   */
  eligibleInputs?: Array<{
    name: string;
    displayName: string;
    dataType: string;
    description?: string;
  }>;
}

export function InteractionSurface({
  request,
  density,
  currentInputs,
  onDismiss,
  billingReadOnly = false,
  adminReadOnly = false,
  eligibleInputs,
}: InteractionSurfaceProps) {
  // Defence-in-depth: suppress in admin read-only context (design §11 risk #17).
  if (adminReadOnly) return null;

  const compact = density === 'narrative';

  return (
    <div
      data-testid="interaction-surface"
      data-kind={request.kind}
      data-density={density}
      className={clsx(
        'border-t border-panel-border bg-panel-bg',
        compact ? 'px-4 py-3' : 'px-6 py-3.5'
      )}
    >
      {/* Header row: prompt label + dismiss button */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          {/* Accent pulse dot — glow has no token alias (audit-exempt per pattern) */}
          <span
            className="w-1.5 h-1.5 rounded-full bg-accent shrink-0"
            style={{ boxShadow: '0 0 4px rgba(0,200,83,0.4)' }} // @design-token accentGlow has no Tailwind alias
          />
          <span className="text-[8px] font-mono font-semibold tracking-[2.5px] uppercase text-text-muted">
            {request.prompt}
          </span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss interaction"
          data-testid="interaction-close"
          className="text-text-dim hover:text-text-muted text-sm font-mono cursor-pointer transition-colors"
        >
          ✕
        </button>
      </div>

      {/* Body: dispatch on kind */}
      {request.kind === 'table-edit' && (
        <TableEditBody
          request={request}
          compact={compact}
          currentInputs={currentInputs}
          onDismiss={onDismiss}
          billingReadOnly={billingReadOnly}
        />
      )}
      {request.kind === 'single-choice' && (
        <SingleChoiceBody
          request={request}
          onDismiss={onDismiss}
          billingReadOnly={billingReadOnly}
        />
      )}
      {request.kind === 'market_default_apply' && (
        <MarketDefaultApplyBody
          request={request}
          conversationId={request.conversationId}
          onDismiss={onDismiss}
          billingReadOnly={billingReadOnly}
          eligibleInputs={eligibleInputs ?? []}
        />
      )}
      {/* multi-choice | free-text | numeric | confirm: declared, not rendered in v1 */}
    </div>
  );
}

// ── table-edit body (FULLY WIRED — re-homes <TableGridEditor> verbatim) ─────

interface TableEditBodyProps {
  request: TableEditInteraction;
  compact: boolean;
  currentInputs?: Record<string, { value: unknown; source?: string }>;
  onDismiss: () => void;
  billingReadOnly: boolean;
}

function TableEditBody({
  request,
  compact,
  currentInputs,
  onDismiss,
  billingReadOnly,
}: TableEditBodyProps) {
  return (
    <div
      className={clsx(
        'overflow-x-auto',
        compact && 'max-h-[220px] overflow-y-auto'
      )}
      data-testid="table-edit-body"
    >
      {/*
       * Re-home `<TableGridEditor>` verbatim (fidelity R6 pixel-close, LOCKED #2).
       * Props: tableDef + conversationId come from the schema-native request shape;
       * currentInputs is forwarded from the host (useConversationViews().inputs shim).
       * applyLabel = "Submit changes" matches the prototype Submit label.
       * onApplied → onDismiss so the host restores the chat composer after success.
       * onDismiss → onDismiss forwarded directly.
       * billingReadOnly → submitDisabled disables the Submit button (the label
       *   also reflects "limit reached" so button state + copy agree).
       */}
      <TableGridEditor
        tableDef={request.tableDef}
        conversationId={request.conversationId}
        currentInputs={currentInputs}
        // overlayCells omitted for the interaction surface (re-edit path, no agent overlay)
        title={request.title ?? request.tableDef.displayName}
        subtitle={request.subtitle}
        applyLabel={
          billingReadOnly ? 'Submit changes (limit reached)' : 'Submit changes'
        }
        dismissLabel="Dismiss"
        submitDisabled={billingReadOnly}
        onApplied={onDismiss}
        onDismiss={onDismiss}
        autoFocus={false}
        testId="interaction-table-grid-editor"
      />
    </div>
  );
}

// ── market_default_apply body (market-default-apply-ux Phase 2 Session 2.3) ──

interface MarketDefaultApplyBodyProps {
  /** Narrowed arm request from the discriminated union. */
  request: MarketDefaultApplyInteraction;
  conversationId: string;
  onDismiss: () => void;
  billingReadOnly: boolean;
  /**
   * Full {name, displayName, dataType, description?}[] from builder.tsx inputDefinitions.
   * B2/I1 — CANNOT be derived from request.options[] which lacks dataType/name.
   * Required for useDefaultMatchSuggestions (server Haiku prompt uses dataType).
   */
  eligibleInputs: Array<{
    name: string;
    displayName: string;
    dataType: string;
    description?: string;
  }>;
}

/**
 * 7th arm: `market_default_apply` — AI suggestions + dropdown + explain + submit.
 *
 * Ported from prototype:551-729 (MarketDefaultApplyArm).
 *
 * Hook call order:
 * 1. useDefaultMatchSuggestions — lazy Haiku suggestion fetch (B2/I1: eligibleInputs
 *    is the PROP, NOT derived from request.options which lacks dataType).
 * 2. useApplyValue — provides applyValue(namedRange, value, {note}) write path (2.6).
 * Both called UNCONDITIONALLY at top (Rules of Hooks).
 */
function MarketDefaultApplyBody({
  request,
  conversationId,
  onDismiss,
  billingReadOnly,
  eligibleInputs,
}: MarketDefaultApplyBodyProps) {
  // Rules of Hooks: both hooks called unconditionally at the top.
  const { suggestions, isLoading } = useDefaultMatchSuggestions({
    conversationId,
    // B2/I1: pass the eligibleInputs PROP (full {name,displayName,dataType,description?}[])
    // — NOT derived from request.options[] which has only {id,label,hint?}, no dataType.
    field: {
      marketField: request.marketField,
      value: request.value,
      unit: request.unit,
    },
    eligibleInputs,
  });

  // useApplyValue is called unconditionally (Rules of Hooks). Cache-read queries are
  // disabled (no metro/categories needed here) — we only need the applyValue writer.
  const { applyValue } = useApplyValue({
    conversationId,
    metro: null,
    categories: [],
    enabled: false,
  });

  const [selected, setSelected] = useState<string | null>(null);
  const [explain, setExplain] = useState('');

  const hasSuggestions = suggestions.length > 0;
  const showFailOpen = !isLoading && !hasSuggestions;

  function handleSubmit() {
    if (!selected) return;
    applyValue(selected, request.value, {
      note: explain || undefined,
    });
    onDismiss();
  }

  return (
    <div
      data-testid="market-default-apply-body"
      data-kind="market_default_apply"
    >
      {/* Suggested-inputs section (prototype:602-676) */}
      <div className="mb-3">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="text-[8px] font-mono font-semibold tracking-[2px] uppercase text-text-dim">
            Suggested inputs
          </span>
          {isLoading && (
            <span className="text-[8px] font-mono text-text-dim animate-pulse">
              · finding matches…
            </span>
          )}
        </div>

        {/* Loading state — skeleton cards; dropdown still renders below */}
        {isLoading && (
          <div className="space-y-1" data-testid="arm-suggestions-loading">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="h-9 rounded-[2px] bg-input-bg border border-panel-border animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Populated — selectable AI suggestion cards */}
        {hasSuggestions && (
          <div className="space-y-1" data-testid="arm-suggestions">
            {suggestions.map((s) => {
              const active = selected === s.namedRange;
              return (
                <button
                  key={s.namedRange}
                  type="button"
                  onClick={() => setSelected(s.namedRange)}
                  data-testid={`suggestion-${s.namedRange}`}
                  className={clsx(
                    'w-full text-left px-2.5 py-1.5 rounded-[2px] border transition-colors cursor-pointer',
                    active
                      ? 'border-accent bg-accent-dim'
                      : 'border-panel-border hover:border-panel-border-hi'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={clsx(
                        'w-1.5 h-1.5 rounded-full shrink-0',
                        active ? 'bg-accent' : 'bg-text-dim'
                      )}
                    />
                    <span
                      className={clsx(
                        'text-[11px] font-mono',
                        active ? 'text-text-bright' : 'text-text'
                      )}
                    >
                      {s.displayName}
                    </span>
                  </div>
                  <span className="block mt-0.5 ml-3.5 text-[10px] font-body text-text-muted leading-tight">
                    {s.reason}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* Fail-open — no suggestions, no error UI (prototype:667-675) */}
        {showFailOpen && (
          <div
            className="text-[10px] font-mono text-text-dim px-1 py-1"
            data-testid="arm-suggestions-fail-open"
          >
            No suggestions — pick an input below.
          </div>
        )}
      </div>

      {/* Dropdown fallback — ALWAYS rendered (prototype:678-696) */}
      {/* Native token-tinted <select> — <Select> primitive's fixed py-3.5 is wrong for compact arm */}
      {/* Precedent: MarketDataApplyPanel (web CLAUDE.md "Native form-control with token tint") */}
      <div className="mb-3">
        <label
          htmlFor="arm-input-dropdown"
          className="block text-[8px] font-mono font-semibold tracking-[2px] uppercase text-text-dim mb-1"
        >
          Or choose any eligible input
        </label>
        <select
          id="arm-input-dropdown"
          value={selected ?? ''}
          onChange={(e) => setSelected(e.target.value || null)}
          data-testid="arm-input-dropdown"
          className="w-full bg-input-bg border border-panel-border rounded-[2px] px-2.5 py-1.5 text-[11px] font-mono text-text focus:border-accent/60 focus:outline-none"
        >
          <option value="">Select an input…</option>
          {request.options.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
              {opt.hint ? ` (${opt.hint})` : ''}
            </option>
          ))}
        </select>
      </div>

      {/* Explain free-text — note field threaded via applyValue (prototype:698-711) */}
      <div className="mb-3.5">
        <label
          htmlFor="arm-explain"
          className="block text-[8px] font-mono font-semibold tracking-[2px] uppercase text-text-dim mb-1"
        >
          Why this input? <span className="text-text-dim/60">(optional)</span>
        </label>
        <textarea
          id="arm-explain"
          value={explain}
          onChange={(e) => setExplain(e.target.value)}
          rows={2}
          placeholder={
            request.explainPrompt ??
            'e.g. Construction debt is priced as SOFR + spread'
          }
          data-testid="arm-explain"
          className="w-full bg-input-bg border border-panel-border rounded-[2px] px-2.5 py-1.5 text-[11px] font-mono text-text placeholder:text-text-dim focus:border-accent/60 focus:outline-none resize-none"
        />
      </div>

      {/* Submit / Dismiss (prototype:713-726) */}
      <div className="flex gap-2">
        <Button
          variant="primary"
          disabled={!selected || billingReadOnly}
          onClick={handleSubmit}
          data-testid="arm-submit"
        >
          {billingReadOnly ? 'Apply value (read-only)' : 'Apply value'}
        </Button>
        <Button variant="ghost" onClick={onDismiss} data-testid="arm-dismiss">
          Dismiss
        </Button>
      </div>
    </div>
  );
}

// ── single-choice body (STUB — proves shell generalises, fidelity-spec §) ────

interface SingleChoiceBodyProps {
  /** Canonical union arm from shared-types (no local mirror — avoids drift). */
  request: SingleChoiceInteraction;
  onDismiss: () => void;
  billingReadOnly: boolean;
}

/**
 * Single-choice stub: renders radio-like option buttons.
 * Submit is intentionally a no-op in v1 (not wired to any agent path).
 * The outer shell dispatch + this body prove the `InteractionSurface` frame
 * generalises beyond `table-edit` per the fidelity-spec § "Generalization".
 */
function SingleChoiceBody({
  request,
  onDismiss,
  billingReadOnly,
}: SingleChoiceBodyProps) {
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <>
      <div
        className="flex flex-col gap-1.5 mb-3"
        data-testid="single-choice-options"
        role="radiogroup"
        aria-label={request.prompt}
      >
        {request.options.map((opt) => {
          const active = selected === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setSelected(opt.id)}
              data-testid={`choice-${opt.id}`}
              className={clsx(
                'flex items-center gap-2.5 text-left px-2.5 py-2 rounded-[2px] border transition-colors cursor-pointer',
                active
                  ? 'border-accent bg-accent-dim'
                  : 'border-panel-border hover:border-panel-border-hi'
              )}
            >
              {/* Radio dot (native-browser: accent-accent tint; no radio primitive exists — §11 risk #17 precedent) */}
              <input
                type="radio"
                readOnly
                checked={active}
                tabIndex={-1}
                aria-hidden="true"
                className="accent-accent shrink-0 pointer-events-none"
              />
              <span
                className={clsx(
                  'text-xs font-mono',
                  active ? 'text-text-bright' : 'text-text'
                )}
              >
                {opt.label}
              </span>
              {opt.hint && (
                <span className="ml-auto text-[11px] font-body text-text-muted">
                  {opt.hint}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2">
        {/*
         * Submit is a STUB in v1 — calls onDismiss (no-op agent path).
         * disabled when !selected or billingReadOnly.
         */}
        <Button
          variant="primary"
          disabled={!selected || billingReadOnly}
          onClick={onDismiss}
          data-testid="interaction-submit"
        >
          Submit answer
        </Button>
        <Button
          variant="ghost"
          onClick={onDismiss}
          data-testid="interaction-dismiss"
        >
          Dismiss
        </Button>
      </div>
    </>
  );
}
